import React from "react";
import PropTypes from "prop-types";
import {
    Row,
    Col,
    FormFeedback,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    FormGroup,
    Input,
    Button,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane
} from "reactstrap";
import { useFormik } from "formik";
import classnames from "classnames";
import { memberSchema, initialMemberValues } from "../../../models/member";
import { FormPhotoUpload, LoadingButton, StatusBadge } from "../../../components/Common";
import { useCEP } from "../../../hooks/useCEP";
import { GENDER_OPTIONS } from "../../../constants/gender";

const MemberFormModal = ({ isOpen, toggle, onSave, data, isSaving }) => {
    const isEdit = !!data?.id;
    const [activeTab, setActiveTab] = React.useState("1");
    const { searchCEP, loading: loadingCEP } = useCEP();

    const formik = useFormik({
        initialValues: initialMemberValues,
        validationSchema: memberSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            onSave(values);
        },
    });

    React.useEffect(() => {
        if (isOpen && data) {
            // Os dados já vêm normalizados do MemberService (via memberDataFromFirestore)
            formik.setValues({
                ...initialMemberValues,
                ...data,
                emergencyContact: { ...initialMemberValues.emergencyContact, ...data.emergencyContact },
                healthInfo: { ...initialMemberValues.healthInfo, ...data.healthInfo }
            });
            setActiveTab("1");
        } else if (isOpen) {
            formik.resetForm();
            setActiveTab("1");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, data]);

    const toggleTab = (tab) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered className="premium-modal">
            <ModalHeader toggle={toggle} className="border-bottom-0 pb-0">
                <div className="d-flex align-items-center">
                    <div className="modal-icon-bg bg-primary-soft me-3">
                        <i className={`mdi ${isEdit ? 'mdi-account-edit-outline' : 'mdi-account-plus-outline'} text-primary font-size-24`}></i>
                    </div>
                    <div>
                        <div className="d-flex align-items-center gap-2">
                            <h5 className="modal-title fw-bold mb-0">{isEdit ? 'Editar Cliente' : 'Novo Cliente'}</h5>
                            {isEdit && <StatusBadge status={formik.values.status} size="sm" className="px-3" />}
                        </div>
                        <p className="text-muted small mb-0">Gestão completa do perfil do membro</p>
                    </div>
                </div>
            </ModalHeader>

            <div className="px-4 pt-3 text-center">
                <Nav tabs className="nav-tabs-custom border-bottom-0 bg-light rounded-pill d-inline-flex mb-3">
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "1" }, "rounded-pill py-2 px-4 border-0")}
                            onClick={() => toggleTab("1")}
                            style={{ cursor: "pointer" }}
                        >
                            <i className="mdi mdi-account-circle-outline me-2 font-size-15"></i>
                            <span className="d-none d-sm-inline-block">Dados Pessoais</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "2" }, "rounded-pill py-2 px-4 border-0")}
                            onClick={() => toggleTab("2")}
                            style={{ cursor: "pointer" }}
                        >
                            <i className="mdi mdi-map-marker-outline me-2 font-size-15"></i>
                            <span className="d-none d-sm-inline-block">Endereço</span>
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={classnames({ active: activeTab === "3" }, "rounded-pill py-2 px-4 border-0")}
                            onClick={() => toggleTab("3")}
                            style={{ cursor: "pointer" }}
                        >
                            <i className="mdi mdi-heart-pulse me-2 font-size-15"></i>
                            <span className="d-none d-sm-inline-block">Saúde e Emergência</span>
                        </NavLink>
                    </NavItem>
                </Nav>
            </div>

            <ModalBody className="p-4 pt-3">
                <TabContent activeTab={activeTab}>
                    {/* TAB 1: DADOS PESSOAIS */}
                    <TabPane tabId="1">
                        <Row>
                            <Col lg={3} className="text-center mb-4">
                                <div className="p-3 bg-light rounded-4 h-100 d-flex flex-column align-items-center justify-content-center border border-dashed">
                                    <Label className="fw-bold text-muted small text-uppercase mb-3">Foto de Perfil</Label>
                                    <FormPhotoUpload
                                        photoPreview={formik.values.photoUrl}
                                        onChange={(url) => formik.setFieldValue("photoUrl", url)}
                                        size="lg"
                                    />
                                </div>
                            </Col>
                            <Col lg={9}>
                                <div className="bg-white p-1">
                                    <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4">
                                        Informações de Identificação
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">Nome</Label>
                                                <Input
                                                    name="firstName"
                                                    placeholder="Ex: João"
                                                    className="bg-light border-0 py-2"
                                                    {...formik.getFieldProps("firstName")}
                                                    value={formik.values.firstName || ""}
                                                    invalid={formik.touched.firstName && !!formik.errors.firstName}
                                                />
                                                <FormFeedback>{formik.errors.firstName}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">Sobrenome</Label>
                                                <Input
                                                    name="lastName"
                                                    placeholder="Ex: Silva"
                                                    className="bg-light border-0 py-2"
                                                    {...formik.getFieldProps("lastName")}
                                                    value={formik.values.lastName || ""}
                                                    invalid={formik.touched.lastName && !!formik.errors.lastName}
                                                />
                                                <FormFeedback>{formik.errors.lastName}</FormFeedback>
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">CPF / Documento</Label>
                                                <Input
                                                    name="document"
                                                    placeholder="000.000.000-00"
                                                    className="bg-light border-0 py-2"
                                                    {...formik.getFieldProps("document")}
                                                    value={formik.values.document || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">Data de Nascimento</Label>
                                                <Input
                                                    type="date"
                                                    name="birthDate"
                                                    className="bg-light border-0 py-2"
                                                    {...formik.getFieldProps("birthDate")}
                                                    value={formik.values.birthDate || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">Gênero / Sexo</Label>
                                                <Input
                                                    type="select"
                                                    name="gender"
                                                    className="bg-light border-0 py-2"
                                                    {...formik.getFieldProps("gender")}
                                                >
                                                    <option value="">Selecione</option>
                                                    {GENDER_OPTIONS.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                    ))}
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        {/* Status removed as requested - saved as lead automatically */}

                                        <Col md={12}>
                                            <hr className="my-2 border-dashed" />
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">Telefone Principal (WhatsApp)</Label>
                                                <div className="input-group bg-light rounded-3 overflow-hidden">
                                                    <span className="input-group-text bg-transparent border-0 pe-0 text-muted">
                                                        <i className="mdi mdi-whatsapp font-size-18"></i>
                                                    </span>
                                                    <Input
                                                        name="phone"
                                                        placeholder="(00) 00000-0000"
                                                        className="bg-transparent border-0 py-2"
                                                        {...formik.getFieldProps("phone")}
                                                        value={formik.values.phone || ""}
                                                        invalid={formik.touched.phone && !!formik.errors.phone}
                                                    />
                                                    <FormFeedback>{formik.errors.phone}</FormFeedback>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase text-truncate d-block">E-mail de Contato</Label>
                                                <div className="input-group bg-light rounded-3 overflow-hidden">
                                                    <span className="input-group-text bg-transparent border-0 pe-0 text-muted">
                                                        <i className="mdi mdi-email-outline font-size-18"></i>
                                                    </span>
                                                    <Input
                                                        type="email"
                                                        name="email"
                                                        placeholder="contato@email.com"
                                                        className="bg-transparent border-0 py-2"
                                                        {...formik.getFieldProps("email")}
                                                        value={formik.values.email || ""}
                                                        invalid={formik.touched.email && !!formik.errors.email}
                                                    />
                                                    <FormFeedback>{formik.errors.email}</FormFeedback>
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </TabPane>

                    {/* TAB 2: ENDEREÇO */}
                    <TabPane tabId="2">
                        <div className="bg-white p-1">
                            <h6 className="text-uppercase font-size-12 fw-bold text-muted mb-4 d-flex align-items-center">
                                <i className="mdi mdi-map-marker-radius-outline me-2 text-primary font-size-16"></i>
                                Localização e Endereço
                            </h6>
                            <Row className="g-3">
                                <Col md={3}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">CEP</Label>
                                        <div className="input-group">
                                            <Input
                                                name="zipCode"
                                                placeholder="00000-000"
                                                className="bg-light border-0 py-2"
                                                value={formik.values.zipCode || ""}
                                                onChange={async (e) => {
                                                    const val = e.target.value;
                                                    formik.setFieldValue("zipCode", val);
                                                    const cleanVal = val.replace(/\D/g, "");
                                                    if (cleanVal.length === 8) {
                                                        const addr = await searchCEP(cleanVal);
                                                        if (addr) {
                                                            formik.setFieldValue("address", addr.street || formik.values.address);
                                                            formik.setFieldValue("neighborhood", addr.neighborhood || formik.values.neighborhood);
                                                            formik.setFieldValue("city", addr.city || formik.values.city);
                                                            formik.setFieldValue("state", addr.state || formik.values.state);
                                                        }
                                                    }
                                                }}
                                            />
                                            {loadingCEP && (
                                                <span className="input-group-text bg-light border-0">
                                                    <i className="mdi mdi-loading mdi-spin text-primary"></i>
                                                </span>
                                            )}
                                        </div>
                                    </FormGroup>
                                </Col>
                                <Col md={3}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Estado (UF)</Label>
                                        <Input
                                            name="state"
                                            placeholder="Ex: SP"
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("state")}
                                            value={formik.values.state || ""}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Cidade</Label>
                                        <Input
                                            name="city"
                                            placeholder="Cidade"
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("city")}
                                            value={formik.values.city || ""}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={4}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Bairro</Label>
                                        <Input
                                            name="neighborhood"
                                            placeholder="Bairro"
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("neighborhood")}
                                            value={formik.values.neighborhood || ""}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Logradouro</Label>
                                        <Input
                                            name="address"
                                            placeholder="Rua, Avenida, Praça..."
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("address")}
                                            value={formik.values.address || ""}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={2}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Nº</Label>
                                        <Input
                                            name="number"
                                            placeholder="999"
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("number")}
                                            value={formik.values.number || ""}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={12}>
                                    <FormGroup>
                                        <Label className="fw-semibold text-muted small text-uppercase">Complemento</Label>
                                        <Input
                                            name="complement"
                                            placeholder="Apto, Bloco, Travessa..."
                                            className="bg-light border-0 py-2"
                                            {...formik.getFieldProps("complement")}
                                            value={formik.values.complement || ""}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </TabPane>

                    {/* TAB 3: SAÚDE E EMERGÊNCIA */}
                    <TabPane tabId="3">
                        <Row className="g-4">
                            <Col md={6}>
                                <div className="p-4 bg-light rounded-4 h-100 border border-dashed border-danger-subtle">
                                    <h6 className="text-danger fw-bold text-uppercase font-size-12 mb-4 d-flex align-items-center">
                                        <i className="mdi mdi-alert-circle-outline me-2 font-size-18"></i>
                                        Contato de Emergência
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase">Nome do Contato</Label>
                                                <Input
                                                    name="emergencyContact.name"
                                                    placeholder="Nome completo"
                                                    className="bg-white border-0 py-2 shadow-sm"
                                                    {...formik.getFieldProps("emergencyContact.name")}
                                                    value={formik.values.emergencyContact?.name || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase">Parentesco</Label>
                                                <Input
                                                    name="emergencyContact.relationship"
                                                    placeholder="Pai, Mãe, Cônjuge..."
                                                    className="bg-white border-0 py-2 shadow-sm"
                                                    {...formik.getFieldProps("emergencyContact.relationship")}
                                                    value={formik.values.emergencyContact?.relationship || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase">Telefone</Label>
                                                <Input
                                                    name="emergencyContact.phone"
                                                    placeholder="(00) 00000-0000"
                                                    className="bg-white border-0 py-2 shadow-sm"
                                                    {...formik.getFieldProps("emergencyContact.phone")}
                                                    value={formik.values.emergencyContact?.phone || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="p-4 bg-light rounded-4 h-100 border border-dashed border-info-subtle">
                                    <h6 className="text-info fw-bold text-uppercase font-size-12 mb-4 d-flex align-items-center">
                                        <i className="mdi mdi-heart-pulse me-2 font-size-18"></i>
                                        Informações de Saúde
                                    </h6>
                                    <Row className="g-3">
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label className="fw-semibold text-muted small text-uppercase">Tipo Sanguíneo</Label>
                                                <Input
                                                    type="select"
                                                    name="healthInfo.bloodType"
                                                    className="bg-white border-0 py-2 shadow-sm"
                                                    {...formik.getFieldProps("healthInfo.bloodType")}
                                                >
                                                    <option value="">Não informado</option>
                                                    <option value="A+">A+</option>
                                                    <option value="A-">A-</option>
                                                    <option value="B+">B+</option>
                                                    <option value="B-">B-</option>
                                                    <option value="AB+">AB+</option>
                                                    <option value="AB-">AB-</option>
                                                    <option value="O+">O+</option>
                                                    <option value="O-">O-</option>
                                                </Input>
                                            </FormGroup>
                                        </Col>
                                        <Col md={12}>
                                            <FormGroup className="mb-0">
                                                <Label className="fw-semibold text-muted small text-uppercase">Observações Médicas / Alergias</Label>
                                                <Input
                                                    type="textarea"
                                                    name="healthInfo.observations"
                                                    rows="4"
                                                    placeholder="Descreva aqui qualquer condição médica importante ou alergias conhecidas..."
                                                    className="bg-white border-0 shadow-sm"
                                                    {...formik.getFieldProps("healthInfo.observations")}
                                                    value={formik.values.healthInfo?.observations || ""}
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </Row>
                    </TabPane>
                </TabContent>
            </ModalBody>

            {formik.submitCount > 0 && !formik.isValid && (
                <div className="px-4 pb-3 text-center">
                    <div className="alert alert-danger py-2 mb-0 border-0 shadow-sm d-inline-flex flex-column align-items-center">
                        <div>
                            <i className="mdi mdi-alert-circle-outline font-size-18 me-2"></i>
                            <span>Existem campos obrigatórios não preenchidos ou incorretos. Revise as abas acima.</span>
                        </div>
                    </div>
                </div>
            )}

            <ModalFooter className="border-top-0 pt-0 bg-white rounded-bottom-4 px-4 pb-4">
                <div className="d-flex w-100 justify-content-between align-items-center">
                    <div className="text-muted small">
                        {activeTab === "1" && "Passo 1 de 3: Perfil Pessoal"}
                        {activeTab === "2" && "Passo 2 de 3: Localização"}
                        {activeTab === "3" && "Passo 3 de 3: Segurança e Saúde"}
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" className="text-muted fw-semibold border-0 rounded-0" onClick={toggle}>
                            Sair sem salvar
                        </Button>
                        <LoadingButton
                            color="primary"
                            className="px-5 py-2 fw-bold shadow-lg rounded-0"
                            onClick={formik.handleSubmit}
                            loading={isSaving}
                        >
                            {isEdit ? 'Salvar Alterações' : 'Finalizar Cadastro'}
                        </LoadingButton>
                    </div>
                </div>
            </ModalFooter>
        </Modal>
    );
};

MemberFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    data: PropTypes.object,
    isSaving: PropTypes.bool,
};

export default MemberFormModal;
